import { Box, useTheme } from "@mui/material";
import { Icon, schemeAiPalette } from "@platform/design-system";
import {
  FieldCommentCorner,
  isOpenReview,
  useCellReviews,
} from "@platform/plan-grid-core";

import {
  RATE_REVIEW_PATH_ATTR,
  RATE_REVIEW_PLAN_ID_ATTR,
} from "../../../utils/rateReviewTarget";

import type { Theme } from "@mui/material";
import type { SystemStyleObject } from "@mui/system";
import type { FC, ReactNode } from "react";

export interface RateReviewCellProps {
  /** Benefit plan owning this rate, and the column the cell sits in. */
  planId: string;
  /**
   * Stable identifier for the cell, from `buildRateFieldPath`. Absent when the
   * cell's rate schedule could not be resolved, which leaves the cell plain.
   */
  fieldPath: string | undefined;
  /** Layout styling from the renderer, such as the shared grid cell borders. */
  sx?: SystemStyleObject<Theme>;
  children: ReactNode;
}

/**
 * A rate cell is filled edge to edge by its input, and the Broker Portal theme
 * paints every `MuiInputBase` root opaque white, so the tint has to reach the
 * input as well or it stays hidden behind it. The input's own hover and focus
 * shades are translucent and still read correctly over the tint.
 */
const reviewedCellSx = (tint: string): SystemStyleObject<Theme> => ({
  backgroundColor: tint,
  "& .MuiInputBase-root": { backgroundColor: "transparent" },
});

/**
 * The rate input is right-aligned, so the pencil sits at the left edge, clear of
 * the value. It ignores the pointer so a click still lands in the input. The
 * catalog icon inherits its colour, so the wrapper sets the edited colour.
 */
const EDITED_ICON_SX = {
  position: "absolute",
  left: 4,
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  pointerEvents: "none",
  zIndex: 1,
} as const;

/**
 * Wraps one editable rate cell so an analyst can flag it for review or mark it
 * validated.
 *
 * The cell shades yellow while a review is open and green once it is validated,
 * shows an orange corner whenever it carries a comment, and shows the "Edited"
 * pencil once an analyst edits the cell or pastes rates onto a plan whose rates
 * the AI extracted, matching the plan editor's treatment. The review and
 * validated flags name opposite states and each write clears the other, so a
 * cell holds at most one; review is checked first only to settle a row that
 * somehow carries both. The data attributes let the grid's context menu resolve
 * which cell was right-clicked.
 */
export const RateReviewCell: FC<RateReviewCellProps> = ({
  planId,
  fieldPath,
  sx,
  children,
}) => {
  const theme = useTheme();
  const aiPalette = schemeAiPalette(theme);
  const { getReview, getEdited } = useCellReviews();

  const review = fieldPath ? getReview(planId, fieldPath) : undefined;
  const isEdited = fieldPath ? getEdited(planId, fieldPath) : false;
  const tint = isOpenReview(review)
    ? aiPalette.reviewBackground
    : review?.isValidated
      ? aiPalette.validatedBackground
      : undefined;

  return (
    <Box
      data-testid="rate-review-cell"
      {...(fieldPath
        ? {
            [RATE_REVIEW_PLAN_ID_ATTR]: planId,
            [RATE_REVIEW_PATH_ATTR]: fieldPath,
          }
        : {})}
      sx={{
        position: "relative",
        ...sx,
        ...(tint ? reviewedCellSx(tint) : {}),
      }}
    >
      {review?.comment && <FieldCommentCorner comment={review.comment} />}
      {isEdited && (
        <Box
          data-testid="rate-cell-edited-icon"
          sx={{ ...EDITED_ICON_SX, color: aiPalette.edited }}
        >
          <Icon name="edited" aria-label="Edited" />
        </Box>
      )}
      {children}
    </Box>
  );
};
